import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  clearNamespace,
  parseQueryState,
  serializeQueryState,
  type DashboardQueryState,
  type DashboardSort,
  type DashboardView,
} from "./queryState";

export interface UseDashboardQueryState {
  state: DashboardQueryState;
  setSearch(search: string): void;
  setFilter(name: string, values: string[]): void;
  /** Apply several filter changes in one update (avoids stale-state clobbering
   *  when clearing/setting multiple fields at once). Empty array clears a field. */
  setFilters(updates: Record<string, string[]>): void;
  setSort(sort: DashboardSort | null): void;
  setPage(page: number): void;
  setPageSize(pageSize: number): void;
  setView(view: DashboardView): void;
  reset(): void;
}

export function useDashboardQueryState(
  namespace = "",
): UseDashboardQueryState {
  const [params, setParams] = useSearchParams();

  const state = useMemo(
    () => parseQueryState(params, namespace),
    [params, namespace],
  );

  const patch = useCallback(
    (next: DashboardQueryState) => {
      // Merge: keep other namespaces' params, replace only this namespace's.
      const merged = clearNamespace(params, namespace);
      for (const [k, v] of serializeQueryState(next, namespace)) {
        merged.set(k, v);
      }
      setParams(merged, { replace: true });
    },
    [params, namespace, setParams],
  );

  const setSearch = useCallback(
    (search: string) => patch({ ...state, search, page: 1 }),
    [patch, state],
  );
  const setFilter = useCallback(
    (name: string, values: string[]) => {
      const filters = { ...state.filters };
      if (values.length) filters[name] = values;
      else delete filters[name];
      patch({ ...state, filters, page: 1 });
    },
    [patch, state],
  );
  const setFilters = useCallback(
    (updates: Record<string, string[]>) => {
      const filters = { ...state.filters };
      for (const [name, values] of Object.entries(updates)) {
        if (values.length) filters[name] = values;
        else delete filters[name];
      }
      patch({ ...state, filters, page: 1 });
    },
    [patch, state],
  );
  const setSort = useCallback(
    (sort: DashboardSort | null) => patch({ ...state, sort, page: 1 }),
    [patch, state],
  );
  const setPage = useCallback(
    (page: number) => patch({ ...state, page }),
    [patch, state],
  );
  const setPageSize = useCallback(
    (pageSize: number) => patch({ ...state, pageSize, page: 1 }),
    [patch, state],
  );
  const setView = useCallback(
    (view: DashboardView) => patch({ ...state, view }),
    [patch, state],
  );
  const reset = useCallback(
    () => setParams(clearNamespace(params, namespace), { replace: true }),
    [params, namespace, setParams],
  );

  return {
    state,
    setSearch,
    setFilter,
    setFilters,
    setSort,
    setPage,
    setPageSize,
    setView,
    reset,
  };
}
